import { BaseProjectProps } from "@custom-types/project-types";
import { useMediaQueryList } from "@hooks/use-media-query";
import { OpenProjectButton } from "@pages/project-details/open-project-button";
import { ProjectOverviewActionButtons } from "@pages/project-details/project-overview/project-overview-action-buttons";
import { ResponsiveProjectButtons } from "@pages/project-details/responsive-project-buttons";
import { hasDescribeOnlyPermission } from "@utils/permission-control/project-permission-control";
import { shouldHideOpenProject } from "@utils/project-utils";
import { useMemo } from "react";

interface Props extends BaseProjectProps {
  /** Flag to show a loading state */
  isLoading: boolean;
}

/**
 * Provides buttons for project details page.
 * On larger screens, shows all buttons normally.
 * On smaller screens, shows a compact version with archive/share/delete in a three dot dropdown menu.
 */
export function ProjectButtons({
  project,
  isLoading,
}: Props): JSX.Element | null {
  const { isScreenSmOrSmaller } = useMediaQueryList();

  const shouldHideOpenProjectButton = useMemo(
    () => shouldHideOpenProject(project),
    [project]
  );

  // Describe-only users cannot open or act on the project
  if (hasDescribeOnlyPermission(project)) {
    return null;
  }

  if (!isScreenSmOrSmaller) {
    return (
      <>
        {!isLoading && <ProjectOverviewActionButtons project={project} />}
        {!shouldHideOpenProjectButton && (
          <OpenProjectButton project={project} isLoading={isLoading} />
        )}
      </>
    );
  }

  // Small screens: vertical dots menu for extra actions
  return (
    <>
      {!shouldHideOpenProjectButton && (
        <OpenProjectButton project={project} isLoading={isLoading} />
      )}
      {!isLoading && <ResponsiveProjectButtons project={project} />}
    </>
  );
}
